import React, { useState, useMemo, useEffect } from 'react';
import { useAppointment } from '../context/AppointmentContext';
import {
  Shield, LogOut, Calendar, Clock, Users, CheckCircle, XCircle,
  Trash2, Settings, ChevronDown, ChevronUp, Eye, AlertCircle,
  BarChart3, Plus, Ban, ToggleLeft, ToggleRight, Bell,
  BookOpen, Edit2, X, LayoutDashboard, Home, Mail, Sparkles, RotateCcw,
  Search, Check, Loader2, Save, Copy
} from 'lucide-react';
import { compressImage } from '../utils/imageCompressor';
import { SERVICE_CATEGORIES } from '../data/services';

// ─── Constants ───────────────────────────────────────────────────────────────
const DAYS_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const ALL_SLOTS = [
  '08:00 AM','09:00 AM','10:00 AM','11:00 AM','12:00 PM',
  '01:00 PM','02:00 PM','03:00 PM','04:00 PM','05:00 PM','06:00 PM',
];
const STATUS_COLORS = {
  pending:   { bg: '#fff3cd', text: '#856404',  label: 'Pending'   },
  confirmed: { bg: '#d1e7dd', text: '#0f5132',  label: 'Confirmed' },
  completed: { bg: '#cfe2ff', text: '#084298',  label: 'Completed' },
  cancelled: { bg: '#f8d7da', text: '#842029',  label: 'Cancelled' },
};
const COVER_GRADIENTS = [
  'linear-gradient(135deg, #6b5ce7 0%, #a78bfa 50%, #06b6d4 100%)',
  'linear-gradient(135deg, #f59e0b 0%, #fbbf24 50%, #f97316 100%)',
  'linear-gradient(135deg, #10b981 0%, #34d399 50%, #06b6d4 100%)',
  'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
  'linear-gradient(135deg, #b45309 0%, #d97706 50%, #fbbf24 100%)',
  'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0ea5e9 100%)',
];

// ─── Admin Login Page ────────────────────────────────────────────────────────
const AdminLogin = () => {
  const { adminLogin } = useAppointment();
  const [adminId, setAdminId]   = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      const ok = adminLogin(adminId, password);
      if (!ok) setError('Incorrect Admin ID or password. Please try again.');
      setLoading(false);
    }, 600);
  };

  return (
    <div className="adm-login-page">
      <div className="adm-login-card">
        <div className="adm-login-logo">
          <Shield size={36} />
        </div>
        <h2 className="adm-login-title">Admin Dashboard</h2>
        <p className="adm-login-sub">Enter your credentials to continue</p>

        <form onSubmit={handleSubmit} className="adm-login-form">
          <div className="adm-login-field">
            <label htmlFor="admin-id-input">Admin ID</label>
            <input
              id="admin-id-input"
              type="text"
              value={adminId}
              onChange={e => { setAdminId(e.target.value); setError(''); }}
              placeholder="e.g. samudra_admin"
              autoFocus
              autoComplete="username"
            />
          </div>
          <div className="adm-login-field">
            <label htmlFor="admin-password-input">Password</label>
            <input
              id="admin-password-input"
              type="password"
              value={password}
              onChange={e => { setPassword(e.target.value); setError(''); }}
              placeholder="••••••••"
              autoComplete="current-password"
            />
          </div>

          {error && (
            <div className="adm-login-error">
              <AlertCircle size={14} /> {error}
            </div>
          )}

          <button
            type="submit"
            className="adm-login-btn"
            disabled={loading}
            id="admin-login-submit"
          >
            {loading ? 'Authenticating…' : 'Login to Dashboard'}
          </button>
        </form>

        <a href="/" className="adm-login-back">
          <Home size={14} /> Back to Website
        </a>
      </div>
    </div>
  );
};

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, icon: Icon, color, sub }) => (
  <div className="adm-stat-card" style={{ borderTopColor: color }}>
    <div className="adm-stat-icon" style={{ color, background: color + '22' }}>
      <Icon size={22} />
    </div>
    <div>
      <div className="adm-stat-value">{value}</div>
      <div className="adm-stat-label">{label}</div>
      {sub && <div className="adm-stat-sub">{sub}</div>}
    </div>
  </div>
);

// ─── Appointment Row ─────────────────────────────────────────────────────────
const ApptRow = ({ appt, onStatus, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const st  = STATUS_COLORS[appt.status] || STATUS_COLORS.pending;
  const fmt = (d) => new Date(d + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });

  return (
    <div className="adm-appt-row">
      <div className="adm-appt-main" onClick={() => setExpanded(e => !e)}>
        <div className="adm-appt-info">
          <strong>{appt.name}</strong>
          <span className="adm-appt-service">{appt.service}</span>
        </div>
        <div className="adm-appt-datetime">
          <span><Calendar size={12}/>{fmt(appt.date)}</span>
          <span><Clock size={12}/>{appt.time}</span>
        </div>
        <div className="adm-appt-badge" style={{ background: st.bg, color: st.text }}>
          {st.label}
        </div>
        <div className="adm-appt-actions" onClick={e => e.stopPropagation()}>
          {appt.status === 'pending' && (<>
            <button className="adm-action confirm" title="Confirm" onClick={() => onStatus(appt.id, 'confirmed')}><CheckCircle size={15}/></button>
            <button className="adm-action cancel"  title="Cancel"  onClick={() => onStatus(appt.id, 'cancelled')}><XCircle size={15}/></button>
          </>)}
          {appt.status === 'confirmed' && (<>
            <button className="adm-action complete" title="Complete" onClick={() => onStatus(appt.id, 'completed')}><CheckCircle size={15}/></button>
            <button className="adm-action cancel"   title="Cancel"   onClick={() => onStatus(appt.id, 'cancelled')}><XCircle size={15}/></button>
          </>)}
          <button className="adm-action delete" title="Delete" onClick={() => onDelete(appt.id)}><Trash2 size={15}/></button>
        </div>
        <div className="adm-expand-icon">
          {expanded ? <ChevronUp size={15}/> : <ChevronDown size={15}/>}
        </div>
      </div>
      {expanded && (
        <div className="adm-appt-detail">
          <div><strong>Email:</strong> {appt.email}</div>
          <div><strong>Phone:</strong> {appt.phone}</div>
          {appt.message && <div><strong>Message:</strong> {appt.message}</div>}
          <div><strong>Ref ID:</strong> <code>{appt.id}</code></div>
          <div><strong>Booked:</strong> {new Date(appt.createdAt).toLocaleString()}</div>
        </div>
      )}
    </div>
  );
};

// ─── Blog Form Modal ─────────────────────────────────────────────────────────
const EMPTY_BLOG = {
  title: '', category: 'Astrology', tag: '', excerpt: '',
  authorName: 'Prakash Upadhyaya', authorInitials: 'PU', authorColor: '#f59e0b',
  coverGradient: COVER_GRADIENTS[0], coverImage: '', date: '', readTime: '', content: '',
};

const BlogFormModal = ({ initial, availableCategories = [], onSave, onClose }) => {
  const [form, setForm] = useState(initial || EMPTY_BLOG);
  const editorRef = React.useRef(null);
  const rteFileInputRef = React.useRef(null);

  // Derive categories list ensuring initial post's category and defaults are present
  const categoryOptions = useMemo(() => {
    const set = new Set(['Astrology', 'Yoga & Wellness', ...availableCategories]);
    if (initial?.category) set.add(initial.category);
    return Array.from(set);
  }, [availableCategories, initial]);

  const [isCustomCategory, setIsCustomCategory] = useState(() => {
    if (!initial?.category) return false;
    return !['Astrology', 'Yoga & Wellness'].includes(initial.category) && !availableCategories.includes(initial.category);
  });
  const [customCategoryName, setCustomCategoryName] = useState(() => {
    if (!initial?.category) return '';
    return !['Astrology', 'Yoga & Wellness'].includes(initial.category) && !availableCategories.includes(initial.category)
      ? initial.category
      : '';
  });

  const [compressingCover, setCompressingCover] = useState(false);
  const [compressingRte, setCompressingRte] = useState(false);
  const [imageStats, setImageStats] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setCompressingCover(true);
      setError('');
      const res = await compressImage(file, { maxWidth: 1280, maxHeight: 850, quality: 0.82 });
      set('coverImage', res.dataUrl);
      setImageStats(res);
    } catch (err) {
      console.error('Image compression error:', err);
      setError('Could not process image: ' + err.message);
    } finally {
      setCompressingCover(false);
      e.target.value = '';
    }
  };

  const handleRteImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setCompressingRte(true);
      const res = await compressImage(file, { maxWidth: 1000, maxHeight: 750, quality: 0.8 });
      execCmd('insertImage', res.dataUrl);
    } catch (err) {
      console.error('RTE image error:', err);
      alert('Could not insert image: ' + err.message);
    } finally {
      setCompressingRte(false);
      e.target.value = '';
    }
  };

  // Sync editor content back into form state
  const syncContent = () => {
    if (editorRef.current) {
      set('content', editorRef.current.innerHTML);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    syncContent();
    setError('');

    if (!form.title.trim()) {
      setError('Please provide a title for the blog post.');
      return;
    }
    if (!form.excerpt.trim()) {
      setError('Please provide a short excerpt for the blog post.');
      return;
    }

    let finalCategory = form.category;
    if (isCustomCategory) {
      if (!customCategoryName.trim()) {
        setError('Please enter your custom category name.');
        return;
      }
      finalCategory = customCategoryName.trim();
    }

    const finalForm = {
      ...form,
      category: finalCategory || 'Astrology',
      content: editorRef.current ? editorRef.current.innerHTML : (form.content || ''),
    };

    try {
      setIsSubmitting(true);
      await onSave(finalForm);
    } catch (err) {
      console.error('Failed to save blog:', err);
      setError(err.message || 'Failed to save blog post. Please check backend connection.');
      setIsSubmitting(false);
    }
  };

  // Rich text toolbar commands
  const execCmd = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    syncContent();
  };

  const insertLink = () => {
    const url = prompt('Enter URL:');
    if (url) execCmd('createLink', url);
  };

  const insertImage = () => {
    const url = prompt('Enter image URL:');
    if (url) execCmd('insertImage', url);
  };

  return (
    <div className="adm-modal-overlay" onClick={onClose}>
      <div className="adm-modal adm-modal-lg" onClick={e => e.stopPropagation()}>
        <div className="adm-modal-header">
          <h3>{initial ? 'Edit Blog Post' : 'Add New Blog Post'}</h3>
          <button className="adm-modal-close" onClick={onClose} disabled={isSubmitting}><X size={18}/></button>
        </div>

        <form className="adm-modal-form" onSubmit={handleSave}>
          {error && (
            <div className="adm-login-error" style={{ margin: '0 0 10px 0' }}>
              <AlertCircle size={15} /> {error}
            </div>
          )}

          {/* Cover preview with image support */}
          <div className="adm-modal-cover-preview" style={{
            background: form.coverImage ? 'none' : form.coverGradient,
            overflow: 'hidden',
            position: 'relative'
          }}>
            {form.coverImage && (
              <img
                src={form.coverImage}
                alt="Cover preview"
                style={{
                  position: 'absolute', inset: 0,
                  width: '100%', height: '100%',
                  objectFit: 'cover'
                }}
              />
            )}
            <span style={{ position: 'relative', zIndex: 1 }}>{form.title || 'Preview'}</span>
          </div>

          {/* Cover image URL and file upload */}
          <div className="adm-form-field">
            <label>Cover Image</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="url"
                value={form.coverImage || ''}
                onChange={e => {
                  set('coverImage', e.target.value);
                  setImageStats(null);
                }}
                placeholder="Paste image URL (https://...) or upload below"
                id="blog-form-coverimage"
                style={{ flex: 1, minWidth: '220px' }}
              />
              <label 
                className="adm-btn-outline" 
                style={{ 
                  margin: 0, 
                  padding: '9px 16px', 
                  fontSize: '0.85rem', 
                  borderRadius: '10px', 
                  cursor: compressingCover ? 'not-allowed' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  whiteSpace: 'nowrap',
                  opacity: compressingCover ? 0.7 : 1
                }}
              >
                {compressingCover ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Optimizing...
                  </>
                ) : (
                  <>
                    <Sparkles size={14} /> Upload & Compress
                  </>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={compressingCover}
                  style={{ display: 'none' }}
                />
              </label>
              {form.coverImage && (
                <button
                  type="button"
                  className="adm-btn-outline"
                  style={{
                    margin: 0,
                    padding: '9px 14px',
                    fontSize: '0.85rem',
                    borderRadius: '10px',
                    color: '#ef4444',
                    borderColor: '#fca5a5',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    set('coverImage', '');
                    setImageStats(null);
                  }}
                >
                  Clear Image
                </button>
              )}
            </div>
            {imageStats && (
              <div style={{ fontSize: '0.8rem', color: '#059669', display: 'flex', alignItems: 'center', gap: '6px', marginTop: '3px' }}>
                <Check size={14} />
                <span>Compressed to <strong>{imageStats.compressedSizeKb} KB</strong> (Saved {imageStats.savedPercent}%) — Instant Upload ready</span>
              </div>
            )}
            <span className="adm-field-hint">Upload any image from phone or PC. It will be automatically optimized to ultra-fast format.</span>
          </div>

          {/* Cover gradient picker */}
          <div className="adm-form-field">
            <label>Cover Theme (fallback)</label>
            <div className="adm-gradient-picker">
              {COVER_GRADIENTS.map((g, i) => (
                <button
                  key={i}
                  type="button"
                  className={`adm-gradient-swatch ${form.coverGradient === g ? 'active' : ''}`}
                  style={{ background: g }}
                  onClick={() => set('coverGradient', g)}
                  title={`Theme ${i + 1}`}
                />
              ))}
            </div>
          </div>

          <div className="adm-modal-grid-2">
            <div className="adm-form-field">
              <label>Title *</label>
              <input
                type="text"
                value={form.title}
                onChange={e => set('title', e.target.value)}
                placeholder="Blog post title"
                required
                id="blog-form-title"
              />
            </div>

            {/* Category with Custom Category option */}
            <div className="adm-form-field">
              <label>Category</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <select
                  value={isCustomCategory ? '__custom__' : form.category}
                  onChange={e => {
                    if (e.target.value === '__custom__') {
                      setIsCustomCategory(true);
                    } else {
                      setIsCustomCategory(false);
                      set('category', e.target.value);
                    }
                  }}
                  id="blog-form-category"
                >
                  {categoryOptions.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                  <option value="__custom__">➕ + Custom Category (Create New)...</option>
                </select>

                {isCustomCategory && (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={customCategoryName}
                      onChange={e => setCustomCategoryName(e.target.value)}
                      placeholder="Enter new category name (e.g. Rituals, Meditation)"
                      autoFocus
                      required
                      id="blog-form-custom-category"
                      style={{ flex: 1, borderColor: '#6b5ce7', background: '#faf5ff' }}
                    />
                    <button
                      type="button"
                      className="adm-btn-outline"
                      style={{ padding: '8px 12px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                      onClick={() => {
                        setIsCustomCategory(false);
                        set('category', categoryOptions[0] || 'Astrology');
                      }}
                    >
                      Use Existing
                    </button>
                  </div>
                )}
              </div>
              <span className="adm-field-hint">Choose an existing category or create a new custom category.</span>
            </div>

            <div className="adm-form-field">
              <label>Tag</label>
              <input
                type="text"
                value={form.tag}
                onChange={e => set('tag', e.target.value)}
                placeholder="e.g. Jyotish, Puja, Wellness"
                id="blog-form-tag"
              />
            </div>
            <div className="adm-form-field">
              <label>Date</label>
              <input
                type="text"
                value={form.date}
                onChange={e => set('date', e.target.value)}
                placeholder="e.g. June 12, 2026 (or leave blank for today)"
                id="blog-form-date"
              />
            </div>
            <div className="adm-form-field">
              <label>Read Time</label>
              <input
                type="text"
                value={form.readTime}
                onChange={e => set('readTime', e.target.value)}
                placeholder="e.g. 5 min read"
                id="blog-form-readtime"
              />
            </div>
            <div className="adm-form-field">
              <label>Author Name</label>
              <input
                type="text"
                value={form.authorName}
                onChange={e => set('authorName', e.target.value)}
                placeholder="Author name"
                id="blog-form-author"
              />
            </div>
          </div>

          <div className="adm-form-field">
            <label>Excerpt *</label>
            <textarea
              value={form.excerpt}
              onChange={e => set('excerpt', e.target.value)}
              placeholder="Short description of the article..."
              rows={3}
              required
              id="blog-form-excerpt"
            />
          </div>

          {/* ── Rich Text Editor ──────────────────────────────────────── */}
          <div className="adm-form-field">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Article Content</label>
              {compressingRte && (
                <span style={{ fontSize: '0.78rem', color: '#6b5ce7', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Loader2 size={12} className="animate-spin" /> Optimizing inserted image...
                </span>
              )}
            </div>
            <div className="adm-rte-container">
              {/* Toolbar */}
              <div className="adm-rte-toolbar">
                <div className="adm-rte-toolbar-group">
                  <button type="button" onClick={() => execCmd('bold')} title="Bold" className="adm-rte-btn"><strong>B</strong></button>
                  <button type="button" onClick={() => execCmd('italic')} title="Italic" className="adm-rte-btn"><em>I</em></button>
                  <button type="button" onClick={() => execCmd('underline')} title="Underline" className="adm-rte-btn" style={{ textDecoration: 'underline' }}>U</button>
                  <button type="button" onClick={() => execCmd('strikeThrough')} title="Strikethrough" className="adm-rte-btn" style={{ textDecoration: 'line-through' }}>S</button>
                </div>
                <div className="adm-rte-toolbar-sep" />
                <div className="adm-rte-toolbar-group">
                  <button type="button" onClick={() => execCmd('formatBlock', 'h2')} title="Heading 2" className="adm-rte-btn">H2</button>
                  <button type="button" onClick={() => execCmd('formatBlock', 'h3')} title="Heading 3" className="adm-rte-btn">H3</button>
                  <button type="button" onClick={() => execCmd('formatBlock', 'p')} title="Paragraph" className="adm-rte-btn">P</button>
                </div>
                <div className="adm-rte-toolbar-sep" />
                <div className="adm-rte-toolbar-group">
                  <button type="button" onClick={() => execCmd('formatBlock', 'blockquote')} title="Blockquote" className="adm-rte-btn">❝</button>
                  <button type="button" onClick={() => execCmd('insertUnorderedList')} title="Bullet List" className="adm-rte-btn">•</button>
                  <button type="button" onClick={() => execCmd('insertOrderedList')} title="Numbered List" className="adm-rte-btn">1.</button>
                </div>
                <div className="adm-rte-toolbar-sep" />
                <div className="adm-rte-toolbar-group">
                  <button type="button" onClick={insertLink} title="Insert Link" className="adm-rte-btn">🔗</button>
                  <button type="button" onClick={insertImage} title="Insert Image URL" className="adm-rte-btn">🔗🖼</button>
                  <button type="button" onClick={() => rteFileInputRef.current?.click()} title="Upload Image File" className="adm-rte-btn" disabled={compressingRte}>📤🖼</button>
                </div>
              </div>

              {/* Hidden file input for RTE image upload */}
              <input
                ref={rteFileInputRef}
                type="file"
                accept="image/*"
                onChange={handleRteImageUpload}
                style={{ display: 'none' }}
              />

              {/* Editable area */}
              <div
                ref={editorRef}
                className="adm-rte-editor"
                contentEditable
                suppressContentEditableWarning
                dangerouslySetInnerHTML={{ __html: form.content || '' }}
                onBlur={syncContent}
                id="blog-form-content"
              />
            </div>
          </div>

          <div className="adm-modal-footer">
            <button type="button" className="adm-btn-outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </button>
            <button
              type="submit"
              className="adm-btn-primary"
              id="blog-form-save"
              disabled={isSubmitting || compressingCover || compressingRte}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> {initial ? 'Saving Changes...' : 'Publishing Post...'}
                </>
              ) : (
                initial ? 'Save Changes' : 'Publish Post'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Blog Management Tab ─────────────────────────────────────────────────────
const BlogManager = () => {
  const { blogs, addBlog, updateBlog, deleteBlog } = useAppointment();
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Derive unique categories from existing blogs
  const allCategories = useMemo(() => {
    const set = new Set(['Astrology', 'Yoga & Wellness']);
    (blogs || []).forEach(b => {
      if (b.category && b.category.trim()) set.add(b.category.trim());
    });
    return Array.from(set);
  }, [blogs]);

  const filteredBlogs = useMemo(() => {
    return (blogs || []).filter(blog => {
      const matchCat = selectedCategory === 'All' || blog.category === selectedCategory;
      const q = searchQuery.toLowerCase().trim();
      const matchSearch = !q ||
        (blog.title && blog.title.toLowerCase().includes(q)) ||
        (blog.excerpt && blog.excerpt.toLowerCase().includes(q)) ||
        (blog.tag && blog.tag.toLowerCase().includes(q)) ||
        (blog.authorName && blog.authorName.toLowerCase().includes(q));
      return matchCat && matchSearch;
    });
  }, [blogs, selectedCategory, searchQuery]);

  const handleAdd = async (formData) => {
    const res = await addBlog(formData);
    if (res && res.success) {
      setShowForm(false);
      showToast('New blog post published successfully! 🎉');
    } else {
      throw new Error(res?.message || 'Failed to publish post.');
    }
  };

  const handleEdit = async (formData) => {
    const res = await updateBlog(editTarget.id, formData);
    if (res && res.success) {
      setEditTarget(null);
      showToast('Blog post updated successfully! ✨');
    } else {
      throw new Error(res?.message || 'Failed to update post.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this blog post? This cannot be undone.')) {
      const res = await deleteBlog(id);
      if (res && res.success) {
        showToast('Blog post deleted.');
      } else {
        alert(res?.message || 'Failed to delete blog post.');
      }
    }
  };

  return (
    <div className="adm-content-body">
      {/* Toast notification */}
      {toast && (
        <div style={{
          position: 'fixed',
          top: '24px',
          right: '24px',
          zIndex: 9999,
          background: toast.type === 'error' ? '#ef4444' : '#10b981',
          color: 'white',
          padding: '12px 22px',
          borderRadius: '12px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          fontWeight: 600,
          fontSize: '0.9rem',
        }}>
          <Check size={18} /> {toast.message}
        </div>
      )}

      <div className="adm-section-header">
        <div>
          <h2 className="adm-section-title">Blog Posts</h2>
          <p className="adm-section-sub">{blogs.length} posts published · Fast upload & custom categories active</p>
        </div>
        <button
          className="adm-btn-primary"
          onClick={() => setShowForm(true)}
          id="blog-add-btn"
        >
          <Plus size={16} /> Add New Post
        </button>
      </div>

      {/* Filter and Search Bar */}
      <div style={{
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap',
        background: '#ffffff',
        padding: '14px 18px',
        borderRadius: '14px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
      }}>
        <div style={{ position: 'relative', flex: 1, minWidth: '220px' }}>
          <Search size={15} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input
            type="text"
            placeholder="Search blogs by title, tag, author..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px 8px 36px',
              borderRadius: '9px',
              border: '1.5px solid #e2e8f0',
              fontSize: '0.85rem',
              outline: 'none',
              background: '#f8fafc'
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => setSelectedCategory('All')}
            style={{
              padding: '6px 14px',
              borderRadius: '20px',
              border: '1.5px solid',
              borderColor: selectedCategory === 'All' ? '#1b126c' : '#e2e8f0',
              background: selectedCategory === 'All' ? '#1b126c' : '#f8fafc',
              color: selectedCategory === 'All' ? 'white' : '#475569',
              fontSize: '0.78rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            All ({blogs.length})
          </button>
          {allCategories.map(cat => {
            const count = (blogs || []).filter(b => b.category === cat).length;
            const isActive = selectedCategory === cat;
            return (
              <button
                key={cat}
                type="button"
                onClick={() => setSelectedCategory(cat)}
                style={{
                  padding: '6px 14px',
                  borderRadius: '20px',
                  border: '1.5px solid',
                  borderColor: isActive ? '#6b5ce7' : '#e2e8f0',
                  background: isActive ? '#6b5ce7' : '#f8fafc',
                  color: isActive ? 'white' : '#475569',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {cat} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Blog list */}
      <div className="adm-blog-list">
        {filteredBlogs.length === 0 ? (
          <div className="adm-empty">
            <BookOpen size={40} />
            <p>{blogs.length === 0 ? 'No blog posts yet. Add your first one!' : 'No blog posts match your search or filter.'}</p>
          </div>
        ) : (
          filteredBlogs.map(blog => (
            <div key={blog.id} className="adm-blog-row">
              {/* Cover swatch / image */}
              {blog.coverImage ? (
                <img
                  src={blog.coverImage}
                  alt={blog.title}
                  className="adm-blog-cover-swatch"
                  style={{ objectFit: 'cover' }}
                />
              ) : (
                <div
                  className="adm-blog-cover-swatch"
                  style={{ background: blog.coverGradient || '#6b5ce7' }}
                />
              )}
              {/* Info */}
              <div className="adm-blog-row-info">
                <strong>{blog.title}</strong>
                <div className="adm-blog-row-meta">
                  <span style={{
                    background: '#ede9fe',
                    color: '#6b5ce7',
                    padding: '2px 8px',
                    borderRadius: '6px',
                    fontWeight: 700,
                    fontSize: '0.75rem'
                  }}>
                    {blog.category}
                  </span>
                  <span>·</span>
                  <span>{blog.date}</span>
                  <span>·</span>
                  <span>{blog.readTime || '4 min read'}</span>
                  {blog.tag && (
                    <>
                      <span>·</span>
                      <span style={{ color: '#0d9488' }}>#{blog.tag}</span>
                    </>
                  )}
                </div>
                <p className="adm-blog-row-excerpt">{blog.excerpt}</p>
              </div>
              {/* Actions */}
              <div className="adm-blog-row-actions">
                <button
                  className="adm-action edit"
                  title="Edit"
                  onClick={() => setEditTarget(blog)}
                  id={`blog-edit-${blog.id}`}
                >
                  <Edit2 size={15}/>
                </button>
                <button
                  className="adm-action delete"
                  title="Delete"
                  onClick={() => handleDelete(blog.id)}
                  id={`blog-delete-${blog.id}`}
                >
                  <Trash2 size={15}/>
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add modal */}
      {showForm && (
        <BlogFormModal
          availableCategories={allCategories}
          onSave={handleAdd}
          onClose={() => setShowForm(false)}
        />
      )}

      {/* Edit modal */}
      {editTarget && (
        <BlogFormModal
          initial={editTarget}
          availableCategories={allCategories}
          onSave={handleEdit}
          onClose={() => setEditTarget(null)}
        />
      )}
    </div>
  );
};

// ─── Schedule Manager ────────────────────────────────────────────────────────
const ScheduleManager = () => {
  const {
    schedule,
    updateSchedule,
    resetScheduleToDefault,
    blockedDates,
    toggleBlockedDate,
    normalizeSchedule,
    defaultSchedule,
  } = useAppointment();

  const [localSchedule, setLocalSchedule] = useState(() => normalizeSchedule ? normalizeSchedule(schedule) : schedule);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type: 'success' | 'error' | 'info', message: '' }
  const [blockInput, setBlockInput] = useState('');

  // Sync from context when schedule updates from backend (if no pending local unsaved edits)
  const normalizedCurrent = useMemo(() => {
    return normalizeSchedule ? normalizeSchedule(schedule) : schedule;
  }, [schedule, normalizeSchedule]);

  const hasChanges = useMemo(() => {
    return JSON.stringify(localSchedule) !== JSON.stringify(normalizedCurrent);
  }, [localSchedule, normalizedCurrent]);

  useEffect(() => {
    if (!hasChanges && normalizedCurrent) {
      setLocalSchedule(normalizedCurrent);
    }
  }, [normalizedCurrent, hasChanges]);

  const showNotification = (type, message) => {
    setFeedback({ type, message });
    setTimeout(() => setFeedback(null), 4000);
  };

  const handleSaveSchedule = async () => {
    try {
      setSaving(true);
      await updateSchedule(localSchedule);
      showNotification('success', '✅ Weekly schedule updated and saved successfully!');
    } catch (err) {
      console.error('Failed to save schedule:', err);
      showNotification('error', `❌ Failed to save schedule: ${err.message || 'Please check backend connection'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDiscardChanges = () => {
    setLocalSchedule(normalizedCurrent);
    showNotification('info', 'Reverted unsaved changes to last saved state.');
  };

  const handleResetToDefault = async () => {
    if (!window.confirm('Reset schedule to standard weekly defaults? (Mon-Fri 9AM-4PM, Sat 10AM-12PM, Sun closed)')) {
      return;
    }
    try {
      setSaving(true);
      if (resetScheduleToDefault) {
        await resetScheduleToDefault();
      } else {
        await updateSchedule(defaultSchedule);
      }
      setLocalSchedule(normalizeSchedule ? normalizeSchedule(defaultSchedule) : defaultSchedule);
      showNotification('success', '🔄 Schedule restored to standard defaults!');
    } catch (err) {
      console.error('Failed to reset schedule:', err);
      showNotification('error', '❌ Failed to reset schedule.');
    } finally {
      setSaving(false);
    }
  };

  const toggleSlot = (dayIdx, slot) => {
    setLocalSchedule(prev => {
      const current = prev[dayIdx] || [];
      const next = current.includes(slot)
        ? current.filter(s => s !== slot)
        : [...current, slot];
      return { ...prev, [dayIdx]: next };
    });
  };

  const handleSelectAllDay = (dayIdx) => {
    setLocalSchedule(prev => ({
      ...prev,
      [dayIdx]: [...ALL_SLOTS],
    }));
  };

  const handleClearDay = (dayIdx) => {
    setLocalSchedule(prev => ({
      ...prev,
      [dayIdx]: [],
    }));
  };

  const handleCopyMondayToWeekdays = () => {
    const monSlots = localSchedule[1] || [];
    setLocalSchedule(prev => ({
      ...prev,
      1: [...monSlots],
      2: [...monSlots],
      3: [...monSlots],
      4: [...monSlots],
      5: [...monSlots],
    }));
    showNotification('success', '📋 Copied Monday slots to Tuesday through Friday!');
  };

  const handleBlock = async () => {
    if (!blockInput) return;
    const isAlreadyBlocked = (blockedDates || []).includes(blockInput);
    try {
      await toggleBlockedDate(blockInput);
      const dateToReport = blockInput;
      setBlockInput('');
      showNotification(
        'success',
        isAlreadyBlocked
          ? `✅ Unblocked date ${dateToReport}`
          : `🚫 Blocked date ${dateToReport}`
      );
    } catch (err) {
      showNotification('error', 'Failed to update blocked date');
    }
  };

  return (
    <div className="adm-content-body">
      <div className="adm-section-header" style={{ flexWrap: 'wrap', gap: 16 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h2 className="adm-section-title" style={{ margin: 0 }}>Schedule Settings</h2>
            {hasChanges ? (
              <span
                style={{
                  background: '#fef3c7',
                  color: '#92400e',
                  fontSize: '0.74rem',
                  fontWeight: 700,
                  padding: '3px 10px',
                  borderRadius: 20,
                  border: '1px solid #fde68a',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5
                }}
              >
                ● Unsaved Changes
              </span>
            ) : (
              <span
                style={{
                  background: '#ecfdf5',
                  color: '#065f46',
                  fontSize: '0.74rem',
                  fontWeight: 600,
                  padding: '3px 10px',
                  borderRadius: 20,
                  border: '1px solid #a7f3d0',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4
                }}
              >
                <Check size={12}/> Up to date
              </span>
            )}
          </div>
          <p className="adm-section-sub">Configure and publish available appointment time slots per day</p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={handleCopyMondayToWeekdays}
            className="adm-btn-outline"
            style={{ fontSize: '0.82rem', padding: '9px 14px' }}
            title="Copy Monday's schedule to Tuesday-Friday"
          >
            <Copy size={15}/> Copy Mon to Weekdays
          </button>

          <button
            type="button"
            onClick={handleResetToDefault}
            className="adm-btn-outline"
            style={{ fontSize: '0.82rem', padding: '9px 14px' }}
            title="Reset schedule to standard defaults"
          >
            <RotateCcw size={15}/> Defaults
          </button>

          {hasChanges && (
            <button
              type="button"
              onClick={handleDiscardChanges}
              className="adm-btn-outline"
              style={{ fontSize: '0.82rem', padding: '9px 14px', color: '#64748b' }}
              title="Discard unsaved edits"
            >
              <X size={15}/> Discard
            </button>
          )}

          <button
            type="button"
            onClick={handleSaveSchedule}
            disabled={saving}
            className="adm-btn-primary"
            style={{
              padding: '9px 22px',
              opacity: saving ? 0.75 : 1,
              background: hasChanges
                ? 'linear-gradient(135deg, #10b981, #059669)'
                : 'linear-gradient(135deg, #6b5ce7, #9b8cf0)',
              boxShadow: hasChanges
                ? '0 4px 16px rgba(16,185,129,0.35)'
                : '0 4px 16px rgba(107,92,231,0.35)',
              cursor: saving ? 'not-allowed' : 'pointer'
            }}
            id="admin-save-schedule-btn"
          >
            {saving ? (
              <>
                <Loader2 size={16} className="adm-spin"/>
                Saving...
              </>
            ) : hasChanges ? (
              <>
                <Save size={16}/>
                Save Schedule *
              </>
            ) : (
              <>
                <Check size={16}/>
                Saved
              </>
            )}
          </button>
        </div>
      </div>

      {/* Feedback banner */}
      {feedback && (
        <div
          style={{
            margin: '0 0 20px 0',
            padding: '12px 18px',
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: feedback.type === 'error' ? '#fef2f2' : feedback.type === 'info' ? '#eff6ff' : '#f0fdf4',
            border: `1.5px solid ${feedback.type === 'error' ? '#fca5a5' : feedback.type === 'info' ? '#bfdbfe' : '#86efac'}`,
            color: feedback.type === 'error' ? '#991b1b' : feedback.type === 'info' ? '#1e40af' : '#166534',
            fontWeight: 600,
            fontSize: '0.88rem'
          }}
        >
          <span>{feedback.message}</span>
          <button
            type="button"
            onClick={() => setFeedback(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Schedule grid */}
      <div className="admin-schedule-grid">
        {DAYS_FULL.map((day, idx) => {
          const daySlots = localSchedule[idx] || [];
          const isClosed = daySlots.length === 0;

          return (
            <div
              key={day}
              className="admin-day-card"
              style={{
                opacity: isClosed ? 0.75 : 1,
                border: isClosed ? '1px dashed #cbd5e1' : '1px solid #f1f5f9'
              }}
            >
              <div className="admin-day-header">
                <div>
                  <span className="admin-day-name">{day}</span>
                  <div style={{ marginTop: 2 }}>
                    <span
                      className="admin-day-count"
                      style={isClosed ? { background: '#f1f5f9', color: '#64748b' } : {}}
                    >
                      {isClosed ? 'Closed' : `${daySlots.length} slots`}
                    </span>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 4 }}>
                  <button
                    type="button"
                    onClick={() => handleSelectAllDay(idx)}
                    style={{
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: 6,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      padding: '2px 7px',
                      color: '#475569',
                      cursor: 'pointer'
                    }}
                    title="Select all slots"
                  >
                    All
                  </button>
                  <button
                    type="button"
                    onClick={() => handleClearDay(idx)}
                    style={{
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: 6,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      padding: '2px 7px',
                      color: '#ef4444',
                      cursor: 'pointer'
                    }}
                    title="Clear all (mark closed)"
                  >
                    Clear
                  </button>
                </div>
              </div>

              <div className="admin-day-slots">
                {ALL_SLOTS.map(slot => {
                  const isActive = daySlots.includes(slot);
                  return (
                    <button
                      key={slot}
                      type="button"
                      onClick={() => toggleSlot(idx, slot)}
                      className={`admin-slot-toggle ${isActive ? 'active' : ''}`}
                    >
                      {isActive ? <ToggleRight size={14}/> : <ToggleLeft size={14}/>}
                      {slot}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Floating Save Bar when changes are unsaved */}
      {hasChanges && (
        <div
          style={{
            position: 'sticky',
            bottom: 20,
            zIndex: 50,
            background: 'white',
            borderRadius: 14,
            padding: '14px 22px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
            border: '1.5px solid #10b981',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 12,
            marginTop: 20,
            marginBottom: 20,
            animation: 'slideUp 0.25s ease'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: '1.1rem' }}>💾</span>
            <span style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>
              You have unsaved changes to the schedule. Click Save to publish.
            </span>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button
              type="button"
              onClick={handleDiscardChanges}
              className="adm-btn-outline"
              style={{ padding: '8px 16px', fontSize: '0.84rem' }}
            >
              Discard
            </button>
            <button
              type="button"
              onClick={handleSaveSchedule}
              disabled={saving}
              className="adm-btn-primary"
              style={{
                padding: '8px 24px',
                fontSize: '0.84rem',
                background: 'linear-gradient(135deg, #10b981, #059669)',
                boxShadow: '0 4px 14px rgba(16,185,129,0.35)'
              }}
            >
              {saving ? <Loader2 size={15} className="adm-spin"/> : <Save size={15}/>}
              Save Schedule Now
            </button>
          </div>
        </div>
      )}

      {/* Block dates */}
      <div className="admin-block-section" style={{ marginTop: 28 }}>
        <h4><Ban size={16}/> Block Specific Dates</h4>
        <p style={{ color: '#64748b', fontSize: '0.82rem', margin: '-8px 0 16px 0' }}>
          Select specific holiday or off dates when no appointments can be booked.
        </p>
        <div className="admin-block-input-row">
          <input
            type="date"
            value={blockInput}
            onChange={e => setBlockInput(e.target.value)}
            id="admin-block-date-input"
          />
          <button type="button" onClick={handleBlock} className="admin-block-btn" id="admin-block-add">
            <Plus size={16}/> Block / Unblock Date
          </button>
        </div>
        {blockedDates && blockedDates.length > 0 && (
          <div className="admin-blocked-list" style={{ marginTop: 14 }}>
            {blockedDates.map(d => (
              <div key={d} className="admin-blocked-chip">
                <Ban size={12}/>
                {new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                <button type="button" onClick={() => toggleBlockedDate(d)} title="Unblock this date"><XCircle size={14}/></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Appointments Tab ────────────────────────────────────────────────────────
const AppointmentsManager = () => {
  const { appointments, updateAppointmentStatus, deleteAppointment } = useAppointment();
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDate, setFilterDate]     = useState('');

  const stats = useMemo(() => ({
    total:     appointments.length,
    pending:   appointments.filter(a => a.status === 'pending').length,
    confirmed: appointments.filter(a => a.status === 'confirmed').length,
    completed: appointments.filter(a => a.status === 'completed').length,
    cancelled: appointments.filter(a => a.status === 'cancelled').length,
  }), [appointments]);

  const filtered = useMemo(() =>
    appointments
      .filter(a => filterStatus === 'all' || a.status === filterStatus)
      .filter(a => !filterDate || a.date === filterDate)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
  [appointments, filterStatus, filterDate]);

  return (
    <div className="adm-content-body">
      <div className="adm-section-header">
        <div>
          <h2 className="adm-section-title">Appointments</h2>
          <p className="adm-section-sub">{stats.total} total bookings</p>
        </div>
      </div>

      {/* Stats */}
      <div className="adm-stats-row">
        <StatCard label="Total"     value={stats.total}     icon={Users}     color="#6b5ce7" sub="all time" />
        <StatCard label="Pending"   value={stats.pending}   icon={Clock}     color="#f59e0b" sub="awaiting" />
        <StatCard label="Confirmed" value={stats.confirmed} icon={CheckCircle} color="#10b981" sub="upcoming" />
        <StatCard label="Completed" value={stats.completed} icon={BarChart3} color="#3b82f6" sub="done" />
        <StatCard label="Cancelled" value={stats.cancelled} icon={XCircle}   color="#ef4444" sub="declined" />
      </div>

      {/* Filters */}
      <div className="admin-filters">
        <div className="admin-filter-group">
          <label>Status</label>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} id="admin-filter-status">
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div className="admin-filter-group">
          <label>Filter by Date</label>
          <input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)} id="admin-filter-date" />
        </div>
        {(filterStatus !== 'all' || filterDate) && (
          <button className="admin-filter-clear" onClick={() => { setFilterStatus('all'); setFilterDate(''); }}>
            Clear Filters
          </button>
        )}
      </div>

      {/* List */}
      <div className="admin-appt-list">
        {filtered.length === 0 ? (
          <div className="admin-empty">
            <Eye size={40}/>
            <p>No appointments match your filters.</p>
          </div>
        ) : (
          filtered.map(appt => (
            <ApptRow
              key={appt.id}
              appt={appt}
              onStatus={updateAppointmentStatus}
              onDelete={deleteAppointment}
            />
          ))
        )}
      </div>
    </div>
  );
};

// ─── Contact Messages Row ────────────────────────────────────────────────────
const MessageRow = ({ msg, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const fmt = (d) => new Date(d).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div className="adm-appt-row">
      <div className="adm-appt-main" onClick={() => setExpanded((e) => !e)}>
        <div className="adm-appt-info">
          <strong>{msg.firstName} {msg.lastName}</strong>
          <span className="adm-appt-service">{msg.email}</span>
        </div>
        <div className="adm-appt-datetime" style={{ minWidth: '180px' }}>
          <span><Clock size={12} />{fmt(msg.createdAt)}</span>
          <span>{msg.phone}</span>
        </div>
        <div className="adm-appt-actions" onClick={(e) => e.stopPropagation()}>
          <button
            className="adm-action delete"
            title="Delete Message"
            onClick={() => onDelete(msg.id)}
          >
            <Trash2 size={15} />
          </button>
        </div>
        <div className="adm-expand-icon">
          {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
        </div>
      </div>
      {expanded && (
        <div className="adm-appt-detail" style={{ borderTop: '1px solid #e2e8f0', background: '#f8fafc', padding: '16px' }}>
          <div><strong>Sender Name:</strong> {msg.firstName} {msg.lastName}</div>
          <div><strong>Email Address:</strong> <a href={`mailto:${msg.email}`} style={{ color: '#6b5ce7', textDecoration: 'underline' }}>{msg.email}</a></div>
          <div><strong>Phone Number:</strong> <a href={`tel:${msg.phone}`} style={{ color: '#6b5ce7', textDecoration: 'underline' }}>{msg.phone}</a></div>
          <div><strong>Submitted At:</strong> {new Date(msg.createdAt).toLocaleString()}</div>
          <div style={{ whiteSpace: 'pre-wrap', background: '#fff', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', marginTop: '16px', color: '#334155', lineHeight: '1.6' }}>
            <strong>Message Content:</strong><br />{msg.message}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Contact Messages Tab ────────────────────────────────────────────────────
const MessagesManager = () => {
  const { messages, deleteMessage } = useAppointment();

  const handleDelete = (id) => {
    if (window.confirm('Delete this message permanently? This cannot be undone.')) {
      deleteMessage(id);
    }
  };

  return (
    <div className="adm-content-body">
      <div className="adm-section-header">
        <div>
          <h2 className="adm-section-title">Contact Messages</h2>
          <p className="adm-section-sub">{messages.length} messages received</p>
        </div>
      </div>

      <div className="admin-appt-list">
        {messages.length === 0 ? (
          <div className="admin-empty">
            <Mail size={40} />
            <p>No messages received yet.</p>
          </div>
        ) : (
          messages.map((msg) => (
            <MessageRow
              key={msg.id}
              msg={msg}
              onDelete={handleDelete}
            />
          ))
        )}
      </div>
    </div>
  );
};

// ─── Service Form Modal ───────────────────────────────────────────────────────
const ServiceModal = ({ initial, categoryId, categories, onSave, onClose }) => {
  const [targetCategory, setTargetCategory] = useState(categoryId || categories[0]?.id || 'rituals');
  const [form, setForm] = useState({
    id: initial?.id || '',
    en: {
      title: initial?.en?.title || '',
      subtitle: initial?.en?.subtitle || '',
      desc: initial?.en?.desc || '',
    },
    np: {
      title: initial?.np?.title || '',
      subtitle: initial?.np?.subtitle || '',
      desc: initial?.np?.desc || '',
    },
  });
  const [error, setError] = useState('');

  const updateField = (lang, field, val) => {
    setForm(prev => ({
      ...prev,
      [lang]: {
        ...prev[lang],
        [field]: val,
      },
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.en.title.trim()) {
      setError('English service title is required.');
      return;
    }
    const finalId = form.id.trim() || form.en.title.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-');
    onSave(targetCategory, { ...form, id: finalId });
  };

  return (
    <div className="adm-modal-overlay" onClick={onClose}>
      <div className="adm-modal" style={{ maxWidth: '680px' }} onClick={e => e.stopPropagation()}>
        <div className="adm-modal-header">
          <h3>{initial ? 'Edit Service' : 'Add New Service'}</h3>
          <button className="adm-modal-close" onClick={onClose}><X size={18} /></button>
        </div>

        <form onSubmit={handleSubmit} className="adm-modal-form">
          {error && <div className="adm-login-error"><AlertCircle size={14} /> {error}</div>}

          {/* Category selection */}
          <div className="adm-field">
            <label style={{ fontWeight: 700, color: '#1e293b' }}>Service Category</label>
            <select
              value={targetCategory}
              onChange={e => setTargetCategory(e.target.value)}
              className="adm-input"
              style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1.5px solid #cbd5e1', background: 'white', fontWeight: 600 }}
            >
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.en.icon} {cat.en.label}
                </option>
              ))}
            </select>
          </div>

          {/* ID field */}
          <div className="adm-field">
            <label style={{ fontWeight: 700, color: '#1e293b' }}>Unique Service Identifier (ID)</label>
            <input
              type="text"
              value={form.id}
              onChange={e => setForm(f => ({ ...f, id: e.target.value }))}
              placeholder="e.g. wedding, griha-pravesh, kundali"
              className="adm-input"
              style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1.5px solid #cbd5e1' }}
            />
            <small style={{ color: '#94a3b8', fontSize: '11px' }}>Auto-generated from title if left blank</small>
          </div>

          {/* English Section */}
          <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '14px', border: '1px solid #e2e8f0' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}>
              🇬🇧 English Details
            </h4>
            <div className="adm-field" style={{ marginBottom: '10px' }}>
              <label>Service Title (English)*</label>
              <input
                type="text"
                value={form.en.title}
                onChange={e => updateField('en', 'title', e.target.value)}
                placeholder="e.g. Hindu Weddings"
                style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
              />
            </div>
            <div className="adm-field" style={{ marginBottom: '10px' }}>
              <label>Subtitle / Tagline (English)</label>
              <input
                type="text"
                value={form.en.subtitle}
                onChange={e => updateField('en', 'subtitle', e.target.value)}
                placeholder="e.g. Vivaha Sanskar"
                style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
              />
            </div>
            <div className="adm-field">
              <label>Description (English)</label>
              <textarea
                rows={3}
                value={form.en.desc}
                onChange={e => updateField('en', 'desc', e.target.value)}
                placeholder="Full ceremony description..."
                style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', resize: 'vertical' }}
              />
            </div>
          </div>

          {/* Nepali Section */}
          <div style={{ background: '#fffbeb', padding: '16px', borderRadius: '14px', border: '1px solid #fef3c7' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '700', color: '#92400e', display: 'flex', alignItems: 'center', gap: '6px' }}>
              🇳🇵 Nepali Details (नेपाली विवरण)
            </h4>
            <div className="adm-field" style={{ marginBottom: '10px' }}>
              <label style={{ color: '#92400e' }}>सेवा शीर्षक (नेपाली)</label>
              <input
                type="text"
                value={form.np.title}
                onChange={e => updateField('np', 'title', e.target.value)}
                placeholder="e.g. हिन्दू विवाह"
                style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #fcd34d' }}
              />
            </div>
            <div className="adm-field" style={{ marginBottom: '10px' }}>
              <label style={{ color: '#92400e' }}>उपशीर्षक (नेपाली)</label>
              <input
                type="text"
                value={form.np.subtitle}
                onChange={e => updateField('np', 'subtitle', e.target.value)}
                placeholder="e.g. विवाह संस्कार"
                style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #fcd34d' }}
              />
            </div>
            <div className="adm-field">
              <label style={{ color: '#92400e' }}>विवरण (नेपाली)</label>
              <textarea
                rows={3}
                value={form.np.desc}
                onChange={e => updateField('np', 'desc', e.target.value)}
                placeholder="वैदिक विधि विवरण..."
                style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #fcd34d', resize: 'vertical' }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '10px' }}>
            <button type="button" onClick={onClose} className="adm-btn-outline">
              Cancel
            </button>
            <button type="submit" className="adm-btn-primary" style={{ padding: '10px 24px' }}>
              Save Service
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Services Manager Component ──────────────────────────────────────────────
const ServicesManager = () => {
  const { servicesList, updateServices, resetServicesToDefault } = useAppointment();
  const categories = (servicesList && servicesList.length > 0) ? servicesList : SERVICE_CATEGORIES;

  const [activeCategoryFilter, setActiveCategoryFilter] = useState('all');
  const [modalState, setModalState] = useState(null); // { service, categoryId, isNew }
  const [feedback, setFeedback] = useState('');

  const totalServices = categories.reduce((sum, c) => sum + (c.services ? c.services.length : 0), 0);

  const showNotification = (msg) => {
    setFeedback(msg);
    setTimeout(() => setFeedback(''), 3500);
  };

  const handleSaveService = async (targetCatId, serviceData) => {
    // Clone categories deeply
    const updated = categories.map(cat => ({
      ...cat,
      services: [...(cat.services || [])],
    }));

    // If editing, remove from original location
    if (!modalState?.isNew) {
      updated.forEach(cat => {
        cat.services = cat.services.filter(s => s.id !== modalState?.service?.id);
      });
    }

    // Add to target category
    const targetCat = updated.find(c => c.id === targetCatId);
    if (targetCat) {
      targetCat.services = targetCat.services.filter(s => s.id !== serviceData.id);
      targetCat.services.push(serviceData);
    }

    await updateServices(updated);
    setModalState(null);
    showNotification('✅ Service saved successfully!');
  };

  const handleDeleteService = async (catId, serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    const updated = categories.map(cat => {
      if (cat.id === catId) {
        return {
          ...cat,
          services: (cat.services || []).filter(s => s.id !== serviceId),
        };
      }
      return cat;
    });

    await updateServices(updated);
    showNotification('🗑️ Service deleted successfully.');
  };

  const handleReset = async () => {
    if (window.confirm('Reset all services to original defaults? Any custom services added will be restored.')) {
      await resetServicesToDefault();
      showNotification('🔄 Services restored to defaults.');
    }
  };

  const displayedCategories = activeCategoryFilter === 'all'
    ? categories
    : categories.filter(c => c.id === activeCategoryFilter);

  return (
    <div className="adm-content-body">
      <div className="adm-section-header" style={{ flexWrap: 'wrap', gap: '14px' }}>
        <div>
          <h2 className="adm-section-title">Services Management</h2>
          <p className="adm-section-sub">
            {totalServices} services across {categories.length} categories • Edits update the live website immediately
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {feedback && (
            <span style={{ fontSize: '13px', fontWeight: '600', color: '#16a34a', background: '#dcfce7', padding: '6px 12px', borderRadius: '8px' }}>
              {feedback}
            </span>
          )}
          <button
            onClick={handleReset}
            className="adm-btn-outline"
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 14px' }}
            title="Reset to default services"
          >
            <RotateCcw size={15} /> Reset Defaults
          </button>
          <button
            onClick={() => setModalState({ service: null, categoryId: categories[0]?.id, isNew: true })}
            className="adm-btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 18px' }}
          >
            <Plus size={16} /> Add New Service
          </button>
        </div>
      </div>

      {/* Category filter pills */}
      <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '6px', marginBottom: '20px' }}>
        <button
          onClick={() => setActiveCategoryFilter('all')}
          style={{
            padding: '8px 16px',
            borderRadius: '10px',
            fontSize: '13px',
            fontWeight: '600',
            border: 'none',
            cursor: 'pointer',
            background: activeCategoryFilter === 'all' ? '#1b126c' : '#f1f5f9',
            color: activeCategoryFilter === 'all' ? 'white' : '#475569',
            transition: 'all 0.2s',
          }}
        >
          All Services ({totalServices})
        </button>
        {categories.map(cat => (
          <button
            key={cat.id}
            onClick={() => setActiveCategoryFilter(cat.id)}
            style={{
              padding: '8px 16px',
              borderRadius: '10px',
              fontSize: '13px',
              fontWeight: '600',
              border: 'none',
              cursor: 'pointer',
              background: activeCategoryFilter === cat.id ? cat.color : '#f1f5f9',
              color: activeCategoryFilter === cat.id ? 'white' : '#475569',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <span>{cat.en?.icon || '🪔'}</span>
            <span>{cat.en?.label || cat.id}</span>
            <span style={{ opacity: 0.8, fontSize: '11px' }}>({cat.services?.length || 0})</span>
          </button>
        ))}
      </div>

      {/* Services grouped by category */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
        {displayedCategories.map(cat => (
          <div key={cat.id} style={{ background: 'white', borderRadius: '18px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
            <div style={{ padding: '16px 22px', background: cat.bg, borderBottom: `1px solid ${cat.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '22px' }}>{cat.en?.icon || '🪔'}</span>
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: cat.color }}>
                    {cat.en?.label} <span style={{ fontSize: '13px', fontWeight: '500', color: '#64748b' }}>({cat.np?.label})</span>
                  </h3>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>{cat.services?.length || 0} services configured</span>
                </div>
              </div>
              <button
                onClick={() => setModalState({ service: null, categoryId: cat.id, isNew: true })}
                style={{
                  background: 'white',
                  border: `1px solid ${cat.border}`,
                  color: cat.color,
                  padding: '6px 14px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <Plus size={14} /> Add to {cat.en?.label?.split(' ')[0] || 'Category'}
              </button>
            </div>

            <div style={{ padding: '18px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
              {(cat.services || []).length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', gridColumn: '1 / -1' }}>
                  No services in this category. Click "Add New Service" above.
                </div>
              ) : (
                cat.services.map(svc => (
                  <div
                    key={svc.id}
                    style={{
                      border: '1px solid #e2e8f0',
                      borderRadius: '14px',
                      padding: '16px',
                      background: '#ffffff',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      transition: 'all 0.2s',
                    }}
                  >
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <div>
                          <div style={{ fontWeight: '800', fontSize: '15px', color: '#1e293b' }}>
                            {svc.en?.title || 'Untitled'}
                          </div>
                          {svc.en?.subtitle && (
                            <div style={{ fontSize: '12px', color: '#64748b', fontStyle: 'italic' }}>
                              {svc.en.subtitle}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            onClick={() => setModalState({ service: svc, categoryId: cat.id, isNew: false })}
                            style={{
                              border: 'none',
                              background: '#f1f5f9',
                              color: '#3b82f6',
                              width: '30px',
                              height: '30px',
                              borderRadius: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer'
                            }}
                            title="Edit Service"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            onClick={() => handleDeleteService(cat.id, svc.id)}
                            style={{
                              border: 'none',
                              background: '#fee2e2',
                              color: '#ef4444',
                              width: '30px',
                              height: '30px',
                              borderRadius: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer'
                            }}
                            title="Delete Service"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>

                      {/* Nepali preview */}
                      <div style={{ background: '#fefce8', padding: '6px 10px', borderRadius: '8px', marginBottom: '10px', border: '1px solid #fef08a' }}>
                        <div style={{ fontSize: '13px', fontWeight: '700', color: '#854d0e' }}>
                          {svc.np?.title || '—'}
                        </div>
                        {svc.np?.subtitle && (
                          <div style={{ fontSize: '11px', color: '#a16207' }}>
                            {svc.np.subtitle}
                          </div>
                        )}
                      </div>

                      <p style={{ fontSize: '12px', color: '#475569', lineHeight: '1.4', margin: '0 0 10px 0' }}>
                        {svc.en?.desc ? (svc.en.desc.length > 110 ? svc.en.desc.slice(0, 110) + '…' : svc.en.desc) : 'No description.'}
                      </p>
                    </div>

                    <div style={{ fontSize: '11px', color: '#94a3b8', borderTop: '1px solid #f1f5f9', paddingTop: '8px', display: 'flex', justifyContent: 'space-between' }}>
                      <span>ID: <code>{svc.id}</code></span>
                      <span style={{ color: cat.color, fontWeight: '600' }}>{cat.en?.label?.split(' ')[0]}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        ))}
      </div>

      {modalState && (
        <ServiceModal
          initial={modalState.service}
          categoryId={modalState.categoryId}
          categories={categories}
          onSave={handleSaveService}
          onClose={() => setModalState(null)}
        />
      )}
    </div>
  );
};

// ─── Dashboard Overview ──────────────────────────────────────────────────────
const DashboardOverview = () => {
  const { appointments, blogs } = useAppointment();
  const pending = appointments.filter(a => a.status === 'pending').length;
  const confirmed = appointments.filter(a => a.status === 'confirmed').length;

  return (
    <div className="adm-content-body">
      <div className="adm-section-header">
        <div>
          <h2 className="adm-section-title">Dashboard Overview</h2>
          <p className="adm-section-sub">Welcome back, Samudra</p>
        </div>
      </div>

      <div className="adm-stats-row">
        <StatCard label="Total Appointments" value={appointments.length} icon={Calendar}  color="#6b5ce7" sub="all time" />
        <StatCard label="Pending Review"     value={pending}            icon={Clock}     color="#f59e0b" sub="action needed" />
        <StatCard label="Confirmed"          value={confirmed}          icon={CheckCircle} color="#10b981" sub="upcoming" />
        <StatCard label="Blog Posts"         value={blogs.length}       icon={BookOpen}  color="#3b82f6" sub="published" />
      </div>

      <div className="adm-overview-panels">
        <div className="adm-overview-card">
          <h4><Clock size={16}/> Recent Appointments</h4>
          <div className="adm-mini-list">
            {appointments.slice(0, 4).map(a => (
              <div key={a.id} className="adm-mini-row">
                <span className="adm-mini-name">{a.name}</span>
                <span className="adm-mini-detail">{a.service}</span>
                <span
                  className="adm-mini-badge"
                  style={{ background: STATUS_COLORS[a.status]?.bg, color: STATUS_COLORS[a.status]?.text }}
                >
                  {STATUS_COLORS[a.status]?.label}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="adm-overview-card">
          <h4><BookOpen size={16}/> Recent Blog Posts</h4>
          <div className="adm-mini-list">
            {blogs.slice(0, 4).map(b => (
              <div key={b.id} className="adm-mini-row">
                <div
                  className="adm-mini-cover"
                  style={{ background: b.coverGradient }}
                />
                <span className="adm-mini-name">{b.title}</span>
                <span className="adm-mini-detail">{b.category}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Admin Dashboard Shell (with sidebar) ───────────────────────────────────
const AdminDashboard = () => {
  const { appointments, messages, adminLogout } = useAppointment();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen]     = useState(false);

  const pending = appointments.filter(a => a.status === 'pending').length;

  const navItems = [
    { id: 'dashboard',    label: 'Dashboard',    icon: LayoutDashboard },
    { id: 'appointments', label: 'Appointments', icon: Calendar,    badge: pending || null },
    { id: 'messages',     label: 'Messages',     icon: Mail,        badge: messages.length || null },
    { id: 'services',     label: 'Services',     icon: Sparkles  },
    { id: 'blog',         label: 'Blog Posts',   icon: BookOpen  },
    { id: 'schedule',     label: 'Schedule',     icon: Settings  },
  ];

  return (
    <div className="adm-shell">
      {/* ── Sidebar ─────────────────────────────────────────────────── */}
      {sidebarOpen && (
        <div
          className="adm-sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <aside className={`adm-sidebar ${sidebarOpen ? 'open' : ''}`}>
        {/* Logo */}
        <div className="adm-sidebar-logo">
          <div className="adm-sidebar-logo-icon">
            <Shield size={22} />
          </div>
          <div>
            <div className="adm-sidebar-logo-title">Admin Panel</div>
            <div className="adm-sidebar-logo-sub">Prakash Upadhyaya</div>
          </div>
        </div>

        {/* Nav */}
        <nav className="adm-sidebar-nav">
          {navItems.map(item => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => { setActiveSection(item.id); setSidebarOpen(false); }}
                className={`adm-nav-item ${activeSection === item.id ? 'active' : ''}`}
                id={`adm-nav-${item.id}`}
              >
                <Icon size={18} />
                <span>{item.label}</span>
                {item.badge > 0 && (
                  <span className="adm-nav-badge">{item.badge}</span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Bottom: back to website + logout */}
        <div className="adm-sidebar-bottom">
          <a href="/" className="adm-sidebar-link" id="adm-back-to-site">
            <Home size={16} /> Back to Website
          </a>
          <button onClick={adminLogout} className="adm-sidebar-logout" id="admin-logout">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </aside>

      {/* ── Main Content ─────────────────────────────────────────────── */}
      <div className="adm-main">
        {/* Top bar */}
        <header className="adm-topbar">
          <button
            className="adm-topbar-hamburger"
            onClick={() => setSidebarOpen(o => !o)}
            aria-label="Toggle sidebar"
          >
            <span /><span /><span />
          </button>

          <div className="adm-topbar-title">
            {navItems.find(n => n.id === activeSection)?.label}
          </div>

          <div className="adm-topbar-right">
            {pending > 0 && (
              <div className="adm-notif-pill">
                <Bell size={14}/>
                {pending} pending
              </div>
            )}
            <button onClick={adminLogout} className="adm-topbar-logout" id="admin-logout-top">
              <LogOut size={15}/> Logout
            </button>
          </div>
        </header>

        {/* Page content */}
        <div className="adm-content">
          {activeSection === 'dashboard'    && <DashboardOverview />}
          {activeSection === 'appointments' && <AppointmentsManager />}
          {activeSection === 'messages'     && <MessagesManager />}
          {activeSection === 'services'     && <ServicesManager />}
          {activeSection === 'blog'         && <BlogManager />}
          {activeSection === 'schedule'     && <ScheduleManager />}
        </div>
      </div>
    </div>
  );
};

// ─── Admin Page Root ─────────────────────────────────────────────────────────
const Admin = () => {
  const { adminLoggedIn } = useAppointment();
  return adminLoggedIn ? <AdminDashboard /> : <AdminLogin />;
};

export default Admin;
